import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { formatChangeValue, formatChangePct } from '../../consts';
import { formatCell } from '../../../utils/numbers';
import css from './SunburstTooltip.css';

const TOOLTIP_DATE_FORMAT = 'MMM D';
const TOOLTIP_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS.TOOLTIP';

const formatPeriodHeader = period => {
    if (!period?.start || !period?.end) return '';
    return `${moment(period.start).format(TOOLTIP_DATE_FORMAT)} – ${moment(period.end).format(TOOLTIP_DATE_FORMAT)}`;
};

const TooltipRow = ({ label, colorCell, sizeCell }) => (
    <>
        <span className={css.tooltipLabel}>{label}</span>
        <span className={css.tooltipValue}>{colorCell}</span>
        <span className={css.tooltipValue}>{sizeCell}</span>
    </>
);

TooltipRow.propTypes = {
    label: PropTypes.string.isRequired,
    colorCell: PropTypes.string.isRequired,
    sizeCell: PropTypes.string.isRequired,
};

const SunburstTooltip = ({ translate, path, firstPeriod, secondPeriod, metricLabels, displayFormats, custom }) => {
    const colorFormat = displayFormats?.color;
    const sizeFormat = displayFormats?.size;
    const { colorP1, colorP2, rawColorChange, rawColorChangePct, sizeP1, sizeP2, rawSizeChange, rawSizeChangePct } =
        custom || {};

    return (
        <div className={css.tooltip}>
            <div className={css.tooltipPath}>{path}</div>
            <div className={css.tooltipGrid}>
                <span />
                <span className={css.tooltipHeader}>{metricLabels?.color ?? ''}</span>
                <span className={css.tooltipHeader}>{metricLabels?.size ?? ''}</span>
                <TooltipRow
                    label={formatPeriodHeader(firstPeriod) || translate(`${TOOLTIP_PREFIX}.ANALYSIS`)}
                    colorCell={formatCell(colorP1, colorFormat)}
                    sizeCell={formatCell(sizeP1, sizeFormat)}
                />
                <TooltipRow
                    label={formatPeriodHeader(secondPeriod) || translate(`${TOOLTIP_PREFIX}.COMPARISON`)}
                    colorCell={formatCell(colorP2, colorFormat)}
                    sizeCell={formatCell(sizeP2, sizeFormat)}
                />
                <TooltipRow
                    label={translate(`${TOOLTIP_PREFIX}.CHANGE`)}
                    colorCell={formatChangeValue(rawColorChange, colorFormat)}
                    sizeCell={formatChangeValue(rawSizeChange, sizeFormat)}
                />
                <TooltipRow
                    label={translate(`${TOOLTIP_PREFIX}.CHANGE_PCT`)}
                    colorCell={formatChangePct(rawColorChangePct)}
                    sizeCell={formatChangePct(rawSizeChangePct)}
                />
            </div>
        </div>
    );
};

SunburstTooltip.propTypes = {
    translate: PropTypes.func.isRequired,
    path: PropTypes.string.isRequired,
    firstPeriod: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
    secondPeriod: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
    metricLabels: PropTypes.shape({ color: PropTypes.string, size: PropTypes.string }),
    displayFormats: PropTypes.shape({ color: PropTypes.object, size: PropTypes.object }),
    custom: PropTypes.shape({
        colorP1: PropTypes.number,
        colorP2: PropTypes.number,
        rawColorChange: PropTypes.number,
        rawColorChangePct: PropTypes.number,
        sizeP1: PropTypes.number,
        sizeP2: PropTypes.number,
        rawSizeChange: PropTypes.number,
        rawSizeChangePct: PropTypes.number,
    }),
};

export default SunburstTooltip;
